import type { SelectMenuOption } from 'src/components/SelectMenu';
import {
  type CustomViewAttribute,
  type CustomViewSorting,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';

export type DisplaySort = Pick<CustomViewSorting, 'id' | 'desc'>;

export const SortDirection = {
  Asc: 'asc',
  Desc: 'desc',
} as const;

export type SortDirectionType = (typeof SortDirection)[keyof typeof SortDirection];

enum SortOrderKind {
  Chronological = 'chronological',
  Alphabetical = 'alphabetical',
  Numeric = 'numeric',
}

interface SortOrderKindConfig {
  naturalDirection: SortDirectionType;
  labels: Record<SortDirectionType, string>;
}

const SORT_ORDER_KIND_CONFIG: Record<SortOrderKind, SortOrderKindConfig> = {
  [SortOrderKind.Chronological]: {
    naturalDirection: SortDirection.Desc,
    labels: { [SortDirection.Desc]: 'Newest first', [SortDirection.Asc]: 'Oldest first' },
  },
  [SortOrderKind.Alphabetical]: {
    naturalDirection: SortDirection.Asc,
    labels: { [SortDirection.Asc]: 'A to Z', [SortDirection.Desc]: 'Z to A' },
  },
  [SortOrderKind.Numeric]: {
    naturalDirection: SortDirection.Desc,
    labels: { [SortDirection.Desc]: 'High to low', [SortDirection.Asc]: 'Low to high' },
  },
};

const UI_TYPE_ORDER_KIND: Partial<Record<UIType, SortOrderKind>> = {
  [UIType.Date]: SortOrderKind.Chronological,
  [UIType.SinceTime]: SortOrderKind.Chronological,
  [UIType.Text]: SortOrderKind.Alphabetical,
  [UIType.List]: SortOrderKind.Alphabetical,
  [UIType.Textarea]: SortOrderKind.Alphabetical,
  [UIType.Chip]: SortOrderKind.Alphabetical,
  [UIType.Chips]: SortOrderKind.Alphabetical,
  [UIType.Avatar]: SortOrderKind.Alphabetical,
};

const getOrderKindConfig = (uiType: UIType): SortOrderKindConfig =>
  SORT_ORDER_KIND_CONFIG[UI_TYPE_ORDER_KIND[uiType] ?? SortOrderKind.Numeric];

export const toSortDirection = (desc: boolean): SortDirectionType =>
  desc ? SortDirection.Desc : SortDirection.Asc;

export const toSortDesc = (direction: string): boolean => direction === SortDirection.Desc;

/** The direction a user expects first for this kind of value, applied whenever the sorted attribute changes. */
export const getNaturalSortDesc = (uiType: UIType): boolean =>
  toSortDesc(getOrderKindConfig(uiType).naturalDirection);

export const getSortOrderOptions = (uiType: UIType): SelectMenuOption[] => {
  const { naturalDirection, labels } = getOrderKindConfig(uiType);
  const otherDirection = toSortDirection(!toSortDesc(naturalDirection));

  return [naturalDirection, otherDirection].map((direction) => ({
    value: direction,
    title: labels[direction],
  }));
};

/** Every sortable attribute, visible column or not, with each group's attributes kept together. */
export const getSortAttributeOptions = (
  attributes: CustomViewAttribute[],
): SelectMenuOption[] => {
  const optionsByGroup = new Map<string, SelectMenuOption[]>();

  attributes
    .filter((attribute) => !!attribute.sortingKey)
    .forEach(({ attribute, attributeLabel, group }) => {
      const groupOptions = optionsByGroup.get(group) ?? [];
      groupOptions.push({ value: attribute, title: attributeLabel, group });
      optionsByGroup.set(group, groupOptions);
    });

  return [...optionsByGroup.values()].flat();
};
